import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDevicePlaneConnections } from '../api/devicePlane.js';
import { downloadCsv } from '../lib/csv.js';

export function DevicePlaneConnections() {
  const { data } = useQuery({
    queryKey: ['dp-connections'],
    queryFn: () => getDevicePlaneConnections(200),
    refetchInterval: 5000,
  });

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink">Device Plane — Connections</h1>
          <p className="text-sm text-muted mt-0.5">
            Live connect / disconnect / bootstrap feed from the gateway, plus unstable devices.
          </p>
        </div>
        <button
          onClick={() => downloadCsv('device-plane-connections', data?.items ?? [])}
          className="px-3 py-1.5 rounded-lg bg-accent text-white text-sm hover:opacity-90"
        >
          Export CSV
        </button>
      </div>

      {data && data.flappers.length > 0 && (
        <div className="bg-warn/10 border border-warn rounded-xl p-4">
          <p className="text-sm font-semibold text-warnink mb-1.5">
            ⚠ Unstable devices (≥6 connect/disconnect transitions in the last hour)
          </p>
          <div className="flex flex-wrap gap-2">
            {data.flappers.map((f) => (
              <Link
                key={f.deviceId}
                to={`/device-plane/fleet/${f.deviceId}`}
                className="px-2.5 py-1 rounded-lg bg-surface border border-warn/40 text-xs font-mono text-warnink hover:bg-surface2"
              >
                {f.serialNumber ?? f.deviceId.slice(0, 8)} · {f.transitionsLastHour}×
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-surface border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted border-b border-grid">
              <th className="px-4 py-2.5">Time</th>
              <th className="px-4 py-2.5">Event</th>
              <th className="px-4 py-2.5">Serial</th>
              <th className="px-4 py-2.5">Device ID</th>
              <th className="px-4 py-2.5">Detail</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((c, i) => (
              <tr key={`${c.ts}-${i}`} className="border-b border-grid hover:bg-surface2">
                <td className="px-4 py-2 text-xs text-muted">{new Date(c.ts).toLocaleString()}</td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      c.event === 'connect'
                        ? 'bg-good/15 text-goodink'
                        : c.event === 'disconnect'
                          ? 'bg-bad/15 text-badink'
                          : 'bg-accent/15 text-[#a9c9f2]'
                    }`}
                  >
                    {c.event}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  {c.deviceId ? (
                    <Link to={`/device-plane/fleet/${c.deviceId}`} className="text-accent hover:underline">
                      {c.serialNumber ?? '—'}
                    </Link>
                  ) : (
                    <span className="text-ink2">{c.serialNumber ?? '—'}</span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-muted">
                  {c.deviceId ? `${c.deviceId.slice(0, 8)}…` : '—'}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-muted">
                  {c.detail ? Object.entries(c.detail).map(([k, v]) => `${k}: ${String(v)}`).join(' · ') : '—'}
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted">
                  No connection events yet — they appear as devices connect to the broker.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
